// ** React Imports **
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ** React-Hook-Form **
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** MUI Components (only Dialog and IconButton) **
import {
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";

// ** Redux **
import { useDispatch, useSelector } from "react-redux";
import { forgotPassword } from "src/redux/actions/AuthAction";

// ** Context **
import { useAuthModal } from "src/context/AuthModalContext";

// ** Constants **
import { FormValidationMessages } from "src/constants/form.const";

// ** Snackbar **
import { useSnackbar } from 'notistack';

// ** Components **
import InputField from "../authentication/InputField";
import LoadingOverlay from "../authentication/LoadingOverlay";

// ** Styles **
import './LoginModal.css';

/**
 * ForgotPasswordModal Component
 * 
 * Modal popup for password reset request
 * Matches the design of LoginModal
 */

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email(FormValidationMessages.EMAIL.invalid)
    .required(FormValidationMessages.EMAIL.required),
});

const ForgotPasswordModal = () => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader } = useSelector((state) => state.auth);
  
  // Auth modal context
  const { 
    isForgotPasswordModalOpen, 
    closeModals, 
    switchToLogin,
    openOtpModal,
  } = useAuthModal();

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isForgotPasswordModalOpen) {
      reset();
    }
  }, [isForgotPasswordModalOpen, reset]);

  /**
   * Handle forgot password form submission
   */
  const onSubmit = async (data) => {
    try {
      const result = await dispatch(forgotPassword(data));
      
      if (result.type === "auth/forgotPassword/fulfilled") {
        enqueueSnackbar(t('otpSent'), {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 2000,
        });
        
        // Open OTP modal instead of navigating
        openOtpModal();
      } else {
        enqueueSnackbar(result.payload || t('failedToSendOtp'), {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 2000,
        });
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      enqueueSnackbar(t('somethingWentWrong'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (!loadingBtnLoader) {
      closeModals();
    }
  };

  const formValues = watch();

  return (
    <Dialog
      open={isForgotPasswordModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: 480,
          backgroundColor: 'white',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '3rem 2.5rem' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loadingBtnLoader}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Loading Overlay */}
        <LoadingOverlay isVisible={loadingBtnLoader} type="forgotPassword" />

        {/* Card Title */}
        <h2 className="login-page-new__title">{t('forgotPasswordButton')}</h2>

        <p style={{ 
          textAlign: 'center', 
          color: '#666', 
          marginBottom: '2rem',
          fontSize: '0.938rem',
          lineHeight: '1.5'
        }}>
          {t('enterEmailToReset')}
        </p>

        {/* Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="login-page-new__form">
          
          {/* Email Input */}
          <InputField
            {...register("email")}
            type="email"
            placeholder={t('enterEmailPlaceholder')}
            value={formValues.email}
            error={errors.email ? errors.email.message : ''}
            required
            autoFocus
          />

          {/* Submit Button */}
          <button
            type="submit"
            className="login-page-new__submit-btn"
            disabled={loadingBtnLoader}
          >
            {loadingBtnLoader ? t('sending') : t('sendResetLink')}
          </button>
        </form>

        {/* Back to Login Link */}
        <div className="login-page-new__signup-link">
          <span className="login-page-new__signup-text">{t('rememberPassword')}</span>
          <button 
            onClick={switchToLogin} 
            disabled={loadingBtnLoader}
            className="login-page-new__signup-link-text"
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: loadingBtnLoader ? 'not-allowed' : 'pointer',
              opacity: loadingBtnLoader ? 0.6 : 1,
            }}
          >
            {t('login')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ForgotPasswordModal;
